import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';
import { tradeSchema } from '../../interfaces/ITrade';

// ============================================================
// Contract
// ============================================================

/**
 * Trades contract - routes under /trades prefix.
 *
 * IMPORTANT: Specific paths (/management, /workers) must come BEFORE parameterized path (/:tradeId).
 *
 * SCOPE — this collection is NOT project data (the per-collection exception table in
 * `docs/project-scope-migration.md`), and `trade.repository.ts` carries no project or
 * organization clause anywhere. There is one trade catalogue for the whole installation.
 *
 * - The four READS are **platform reference** (`docs/authorization-scopes.md`): readable by
 *   any authenticated user, deliberately ungated by role. The frontend's profession dialog
 *   and d-orca's worker roster both call `/trades/workers` with an ORDINARY user token, so
 *   a role gate here would be an outage rather than a tightening. Pinned in
 *   `Entities/src/tests/unit/fastify/trades.routes.test.ts`.
 * - The two WRITES are **global admin**, `allowTrusstorAdminRoles`. A trade rename fans out
 *   to professions, managers, activities, report rows, safety events and static tags across
 *   every organization and project — the origin of the five `updateTrade`/`updateTagsTrade`
 *   cascade entries in `PROJECT_LESS_BY_DESIGN`.
 *
 * Neither list changes the paths: no route here becomes `/projects/:projectId/…`.
 */
export const tradesContract = defineContract('trades', {
	getAllTrades: route({
		method: 'GET',
		path: '/',
		response: z.array(tradeSchema),
		operationId: 'getAllTrades',
		tags: ['Trades'],
		summary: 'Get all trades',
	}),
	getManagementTrades: route({
		method: 'GET',
		path: '/management',
		response: z.array(tradeSchema),
		operationId: 'getManagementTrades',
		tags: ['Trades'],
		summary: 'Get management trades',
	}),
	getWorkersTrades: route({
		method: 'GET',
		path: '/workers',
		response: z.array(tradeSchema),
		operationId: 'getWorkersTrades',
		tags: ['Trades'],
		summary: 'Get workers trades',
	}),
	getTradeById: route({
		method: 'GET',
		path: '/:tradeId',
		params: z.object({ tradeId: requiredStringSchema }),
		response: tradeSchema,
		operationId: 'getTradeById',
		tags: ['Trades'],
		summary: 'Get trade by ID',
	}),
	createTrade: route({
		method: 'POST',
		path: '/',
		body: tradeSchema.partial(),
		response: z.string(),
		operationId: 'createTrade',
		tags: ['Trades'],
		summary: 'Create a new trade',
	}),
	updateTrade: route({
		method: 'PUT',
		path: '/',
		body: tradeSchema.partial(),
		response: z.string(),
		operationId: 'updateTrade',
		tags: ['Trades'],
		summary: 'Update a trade',
	}),
});
